import React, { useRef, useState, useMemo } from 'react'
import { Chart as ChartJS, RadialLinearScale, PointElement, LineElement, Filler, Tooltip, Legend } from 'chart.js'
import { Radar } from 'react-chartjs-2'
import { PipeDataStructure, ChartDataPoint } from '../types/pipe'
import './PolarRadiusChart.css'
import { Button } from '../../../../../../../components/ui/button'

ChartJS.register(RadialLinearScale, PointElement, LineElement, Filler, Tooltip, Legend)

interface PolarRadiusChartProps {
  data: PipeDataStructure
}

const PolarRadiusChart: React.FC<PolarRadiusChartProps> = ({ data }) => {
  const chartRef = useRef<ChartJS<'radar'>>(null)
  const [isFullScreen, setIsFullScreen] = useState(false)

  const hasInner = data.pipeinfo?.inner_radius_data === true
  const hasOuter = data.pipeinfo?.outer_radius_data === true
  const inner_radius = data.Data?.inner_radius ?? []
  const outer_radius = data.Data?.outer_radius ?? []
  const phi = data.Data?.phi ?? []

  // Store sampled data for tooltip access
  const sampledDataRef = useRef<{ normal: ChartDataPoint[], fullDetail: ChartDataPoint[] }>({
    normal: [],
    fullDetail: []
  })

  // Convert phi angles to degrees and create chart data
  const createChartData = (fullDetail = false) => {
    // For full screen, show all data points. For normal view, sample every 10 degrees
    const sampledData: ChartDataPoint[] = []
    const sampleInterval = fullDetail ? 1 : 10
    const totalPoints = phi.length

    for (let i = 0; i < totalPoints; i += sampleInterval) {
      const angleDegrees = (phi[i] * 180 / Math.PI + 180) % 360 // Convert radians to degrees
      const ir = hasInner && inner_radius[i] != null ? Number(inner_radius[i]) : null
      const er = hasOuter && outer_radius[i] != null ? Number(outer_radius[i]) : null
      sampledData.push({
        angle: phi[i],
        angleDegrees,
        internalRadius: hasInner && ir != null ? ir : 0,
        externalRadius: hasOuter && er != null ? er : 0,
        wallThickness: ir != null && er != null ? er - ir : 0
      })
    }

    // Store sampled data for tooltip access
    if (fullDetail) {
      sampledDataRef.current.fullDetail = sampledData
    } else {
      sampledDataRef.current.normal = sampledData
    }

    // For full detail view, show labels every 30 degrees. For normal view, every 30 degrees too
    const labels = sampledData.map((point, index) => {
      if (fullDetail) {
        return (index % 30 === 0) ? `${Math.round(point.angleDegrees)}°` : ''
      } else {
        return (index % 3 === 0) ? `${Math.round(point.angleDegrees)}°` : ''
      }
    })

    const datasets = [
      ...(hasInner
        ? [{
            label: 'Internal Radius (mm)',
            data: sampledData.map(point => point.internalRadius),
            borderColor: fullDetail ? 'rgba(74, 144, 226, 1.0)' : 'rgba(74, 144, 226, 1.0)',
            backgroundColor: fullDetail ? 'rgba(74, 144, 226, 0.5)' : 'rgba(74, 144, 226, 0.4)',
            borderWidth: fullDetail ? 6 : 5,
            fill: false,
            pointRadius: fullDetail ? 2 : 4,
            pointHoverRadius: fullDetail ? 4 : 7,
          }]
        : []),
      ...(hasOuter
        ? [{
            label: 'External Radius (mm)',
            data: sampledData.map(point => point.externalRadius),
            borderColor: 'rgba(255, 107, 53, 0.9)',
            backgroundColor: 'rgba(255, 107, 53, 0.2)',
            borderWidth: fullDetail ? 5 : 4,
            fill: false,
            pointRadius: fullDetail ? 1 : 3,
            pointHoverRadius: fullDetail ? 3 : 6,
          }]
        : [])
    ]

    return { labels, datasets }
  }

  const createOptions = (fullDetail = false) => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
          legend: {
            position: 'top' as const,
            labels: {
              color: '#000000',
              font: {
                size: 12
              },
              usePointStyle: true,
              padding: 20
            }
          },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: 'white',
        bodyColor: 'white',
        borderColor: 'rgba(255, 255, 255, 0.3)',
        borderWidth: 1,
        callbacks: {
          title: function(context: any) {
            // Get the angle from stored sampledData using dataIndex
            const dataIndex = context[0].dataIndex
            const currentSampledData = fullDetail ? sampledDataRef.current.fullDetail : sampledDataRef.current.normal
            if (currentSampledData && currentSampledData[dataIndex]) {
              const angleDegrees = currentSampledData[dataIndex].angleDegrees
              return `Angle: ${angleDegrees.toFixed(1)}°`
            }
            return `Angle: ${context[0].label || 'N/A'}`
          },
          label: function(context: any) {
            return `${context.dataset.label}: ${context.parsed.r.toFixed(2)} mm`
          }
        }
      }
    },
        scales: {
          r: {
            beginAtZero: false,
            min: (() => {
              const pad = fullDetail ? 3 : 2
              const innerMin = inner_radius.length > 0 ? Math.min(...inner_radius.map((v: number | string) => Number(v))) : null
              const outerMin = outer_radius.length > 0 ? Math.min(...outer_radius.map((v: number | string) => Number(v))) : null
              const minVal = [innerMin, outerMin].filter((v): v is number => v != null && Number.isFinite(v))
              return minVal.length > 0 ? Math.min(...minVal) - pad : 0
            })(),
            max: (() => {
              const pad = fullDetail ? 3 : 2
              const innerMax = inner_radius.length > 0 ? Math.max(...inner_radius.map((v: number | string) => Number(v))) : null
              const outerMax = outer_radius.length > 0 ? Math.max(...outer_radius.map((v: number | string) => Number(v))) : null
              const maxVal = [innerMax, outerMax].filter((v): v is number => v != null && Number.isFinite(v))
              return maxVal.length > 0 ? Math.max(...maxVal) + pad : 100
            })(),
            ticks: {
              color: 'rgba(0, 0, 0, 0.8)',
              font: {
                size: fullDetail ? 12 : 10
              },
              stepSize: fullDetail ? 3 : 2, // Larger steps for full screen
              callback: function(value: number | string) {
                const numValue = Number(value)
                // Only show whole numbers and hide any value above 237
                if (numValue > 237 || numValue % 1 !== 0) {
                  return ''
                }
                return numValue
              }
            },
            grid: {
              color: 'rgba(0, 0, 0, 0.2)',
            },
            angleLines: {
              color: 'rgba(0, 0, 0, 0.2)',
            },
            pointLabels: {
              color: 'rgba(0, 0, 0, 0.7)',
              font: {
                size: fullDetail ? 11 : 10
              }
            }
          }
        },
    animation: {
      duration: fullDetail ? 2000 : 1500,
      easing: 'easeInOutCubic' as const
    }
  })

  return (
    <>
      <div className="polar-chart-container">
        <div className="chart-wrapper">
          <Radar ref={chartRef} data={createChartData()} options={createOptions()} />
        </div>
        <Button 
          className="fullscreen-btn"
          onClick={() => setIsFullScreen(true)}
          title="View in full screen with all 360° data points"
        >
          🔍 Full Screen Analysis
        </Button>
      </div>

      {/* Full Screen Modal - Portal to body */}
      {isFullScreen && (
        <div className="fullscreen-modal-overlay">
          <div className="fullscreen-modal-backdrop" onClick={() => setIsFullScreen(false)} />
          <div className="fullscreen-modal-content">
            <div className="fullscreen-header">
              <div className="header-left">
                <h2>360° Polar Analysis - Detailed View</h2>
                <p>All {data.Data.phi.length} measurement points • Every degree precision</p>
              </div>
              <Button 
                className="close-btn"
                onClick={() => setIsFullScreen(false)}
              >
                ✕ Close
              </Button>
            </div>
            <div className="fullscreen-chart">
              <Radar data={createChartData(true)} options={createOptions(true)} />
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default PolarRadiusChart
